import { getSeriesWindowing } from "../utils/dicomVoi";
/**
 * LabelingPage - Manual labeling workflow using shared viewer components
 *
 * Reuses CaseViewer architecture:
 * - LeftSidebar: Series thumbnails and window presets
 * - HangingProtocolGrid: Multi-viewport layout (2x2)
 * - LabelingPanel: Right panel for label assignment (only unique component)
 *
 * URL Parameters:
 * - case=N: Load case by index (0-based)
 * - study=UID: Load case by study_instance_uid (for bookmarking)
 */

import { useEffect, useCallback, useState, useMemo, useRef } from "react";
import { useSearchParams, Navigate, useNavigate } from "react-router-dom";

import { useAtom, useSetAtom, useAtomValue } from "jotai";
import { worklistAtom, currentCaseIndexAtom, studyRowId } from "../atoms/labelingAtoms";
import { userIdAtom } from "../atoms/authAtoms";

import {
  leftSidebarCollapsedAtom,
  rightSidebarCollapsedAtom,
  reportPaneWidthAtom,
  REPORT_PANE_MIN_WIDTH,
  REPORT_PANE_MAX_WIDTH,
} from "../atoms/uiAtoms";
import {
  hangingProtocolActionsAtom,
  activeSlotAtom,
  slotStateFamily,
  displayedSourceFamily,
} from "../atoms/hangingProtocolAtoms";
import {
  useLabelingQueries,
  useStudyMetadata,
  formatMetadataProgress,
  useCasesQuery,
  useMarkCompleteMutation,
} from "../hooks/useLabelingQueries";
import {
  isFinishedCaseStatus,
  isSkippedByNavigation,
  useNextUnlabeledCase,
} from "../hooks/useNextUnlabeledCase";
import { useViewerHotkeys } from "../hooks/useViewerHotkeys";
import { resolveLabelingViewerRoute } from "../utils/labelingViewerRoute";
import { listUrl } from "../types/jointReview";
import { labelingViewerSurfaces } from "../utils/viewerSurfaces";
import { useLabelingRole } from "../hooks/useLabelingRole";
import { initializeCornerstone } from "../utils/dicomLoader";
import { emit } from "../lib/viewerBus";
import { nnInteractiveLoadingAtom, nnSessionActiveAtom } from "../atoms/segmentationAtoms";
import {
  setNnToastCallback,
  setNnInteractiveLoadingCallback,
  setNnSessionActiveCallback,
} from "../tools/nnInteractiveSession";
import { shouldAutoInvert } from "../utils/dicomMetadataLoader";
import { derivePlane } from "../utils/seriesClassification";
import { US3D_REGEX } from "../utils/studyGroups";
import { categoryForProjectType } from "../utils/labelingCategories";
import { canViewStats } from "../utils/statsGate";
import { caseLockReason } from "../utils/caseStatus";
import { useCallerEmail } from "../hooks/useCallerEmail";
import { warnOnMixedSignedTransferSyntax } from "../utils/signCorrectionTelemetry";
import { LabelingPanel } from "./labeling/LabelingPanel";
import CaseViewer from "./CaseViewer";
import { AdjudicationPanel } from "./labeling/AdjudicationPanel";
import { SegmentationAnnotationPanel } from "./labeling/SegmentationAnnotationPanel";
import { AttributeLabelingPanel } from "./labeling/AttributeLabelingPanel";
import { StudyObservationPanel } from "./labeling/StudyObservationPanel";
import { ReportingRightPanel } from "./labeling/ReportingRightPanel";
import { AnnotationSpeedDashboard } from "./labeling/AnnotationSpeedDashboard";
import { useCaseSessionTimer } from "../hooks/useCaseSessionTimer";
import { CaseEventLogContext, useCaseEventLog } from "../hooks/useCaseEventLog";
import {
  useStudyAssignment,
  findAssignmentForStudy,
  type StudyAssignmentRow,
} from "../hooks/useStudyAssignment";
import { useReportState } from "../hooks/useStudyQueries";
import { useStudyEventLog, type Difficulty } from "../hooks/useStudyEventLog";
import { useIdleTimer } from "../hooks/useIdleTimer";
import { useActiveReadingTimer } from "../hooks/useActiveReadingTimer";
import { useToken } from "../context/AuthContext";
import { useSubmitReaderFeedback } from "../hooks/useReporterMutations";
import { StudyPostCaseSurvey } from "./study/StudyPostCaseSurvey";
import { StudyPracticeGate } from "./study/StudyPracticeGate";
import {
  onboardingPhaseAtom,
  practiceSignedCountAtom,
  canBeginStudyAtom,
  type OnboardingPhase,
} from "../atoms/studyOnboardingAtoms";
import { StudyLockScreen } from "./study/StudyLockScreen";
import { StudyDifficultyModal } from "./study/StudyDifficultyModal";
import type {
  StudyInitialDocContext,
  StudySignConfirmation,
  StudySignContext,
} from "./PrototypeReport";
import { LeftSidebar, WindowPreset, CollapsedSidebarStrip } from "./sidebar";
import { HangingProtocolGrid, PhaseDependentLayout, SliceDependentGrid } from "./hanging-protocol";
import { projectGatingModeAtom, availableGatingModesAtom } from "../atoms/gatingAtoms";
import { availableGatingModes, defaultGatingMode } from "../config/annotationSchemas";
import { useLabelingViewerSurfaces } from "../hooks/useViewerSurfaces";
import type { NativePredictions } from "../types/predictions";
import { GATING_MODE } from "../types/gating";
import {
  FullScreenLoading,
  FullScreenError,
  FullScreenEmpty,
  ViewerLoading,
  ViewerError,
  SeriesPicker,
  NavigationButtons,
} from "./common";

export function LabelingPage() {
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get("project");
  // Redirect before mounting anything that depends on projectId — this keeps
  // every hook below seeing a guaranteed non-null projectId and avoids any
  // "" / fallback sentinel in the data layer.
  if (!projectId) {
    return <Navigate to="/labeling-overview" replace />;
  }
  return <LabelingPageInner projectId={projectId} />;
}

function LabelingPageInner({ projectId }: { projectId: string }) {
  const [searchParams] = useSearchParams();

  const [worklist, setWorklist] = useAtom(worklistAtom);
  const [currentIndex, setCurrentIndex] = useAtom(currentCaseIndexAtom);
  const { worklistQuery } = useLabelingQueries(projectId);
  const annotationReadOnly = worklistQuery.data?.annotation_read_only === true;

  // Sidebar collapse state (shared atoms, persisted to localStorage)
  const [leftSidebarCollapsed, setLeftSidebarCollapsed] = useAtom(leftSidebarCollapsedAtom);
  const [rightSidebarCollapsed, setRightSidebarCollapsed] = useAtom(rightSidebarCollapsedAtom);

  // Drag-to-resize the reporting drafter pane (radiologists asked for more
  // report space). Persisted width + pointer-capture drag, mirroring the
  // dictation viewer's right-sidebar resize in CaseViewer. Only the reporting
  // pane is resizable; labeling/adjudication panels keep their fixed width.
  const [reportPaneWidth, setReportPaneWidth] = useAtom(reportPaneWidthAtom);
  const handleReportPaneResize = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      const startX = e.clientX;
      const startWidth = reportPaneWidth;
      const target = e.currentTarget;
      target.setPointerCapture(e.pointerId);

      const onMove = (moveEvent: PointerEvent) => {
        // Pane is on the right; dragging left grows it.
        const next = startWidth + (startX - moveEvent.clientX);
        const clamped = Math.max(REPORT_PANE_MIN_WIDTH, Math.min(REPORT_PANE_MAX_WIDTH, next));
        setReportPaneWidth(clamped);
      };
      const onUp = () => {
        target.releasePointerCapture(e.pointerId);
        target.removeEventListener("pointermove", onMove);
        target.removeEventListener("pointerup", onUp);
      };
      target.addEventListener("pointermove", onMove);
      target.addEventListener("pointerup", onUp);
    },
    [reportPaneWidth, setReportPaneWidth],
  );

  const navigate = useNavigate();
  const labelingRole = useLabelingRole();
  const handleWorkList = useCallback(() => {
    // US3D batches have their own study home (SegStudyHome) — route there so the
    // batch's details panel is already expanded when the user returns to pick
    // a different case. Other projects continue to the new study view route,
    // categorized from the loaded worklist's project_type (populated from
    // useWorklistQuery via the worklist atom — see LabelingPageInner above).
    //
    // The non-US3D destination differs by role: `projectId` here is the
    // WORKLIST id, which only maps to a route for annotators (CategoryView's
    // `:projectId` → StudyView). For admin/internal, that same id doesn't
    // match a base project id, so routing to `.../:projectId` 404s into
    // AdminProjectDetail's "not found" panel — send those roles to the
    // category view instead (control room / internal list).
    const category = categoryForProjectType(worklist?.project_type);
    const dest = US3D_REGEX.test(projectId)
      ? `/labeling/urinary-stone?project=${encodeURIComponent(projectId)}`
      : labelingRole === "annotator"
        ? `/labeling-overview/${category}/${encodeURIComponent(projectId)}`
        : `/labeling-overview/${category}`;
    navigate(dest);
  }, [navigate, projectId, worklist?.project_type, labelingRole]);
  const [showDashboard, setShowDashboard] = useState(false);
  const userId = useAtomValue(userIdAtom);
  const canViewDashboard = canViewStats(userId);
  const authToken = useToken();
  const submitFeedbackMutation = useSubmitReaderFeedback(authToken);

  // Reader-study onboarding / practice phase atoms. Only relevant in study mode.
  const [onboardingPhase, setOnboardingPhase] = useAtom(onboardingPhaseAtom);
  const [practiceSignedCount, setPracticeSignedCount] = useAtom(practiceSignedCountAtom);
  const canBeginStudy = useAtomValue(canBeginStudyAtom);

  // Eagerly initialize Cornerstone so tools are ready when the viewport mounts.
  // Idempotent — safe to call even if Worklist already triggered initialization.
  useEffect(() => {
    initializeCornerstone().catch((err) =>
      console.warn("[LabelingPage] Cornerstone init failed:", err),
    );
  }, []);

  // Wire the tool-side showNnToast helper to the page-level toast bus. The
  // PageToast host (mounted once at the App layout) subscribes to the `toast`
  // channel and handles auto-dismiss.
  useEffect(() => {
    setNnToastCallback((message) => emit("toast", { message }));
    return () => setNnToastCallback(null);
  }, []);

  // Bridge tool-side nnInteractive state into Jotai so the VolumeViewport can
  // render a spinner overlay while a request is in flight, and useSegmentationCursor
  // can swap the cursor to green/red on Shift/Ctrl. Without these, the runtime
  // fires the callbacks but no subscriber is listening — silent no-op UX.
  const setNnLoading = useSetAtom(nnInteractiveLoadingAtom);
  const setNnSessionActive = useSetAtom(nnSessionActiveAtom);
  useEffect(() => {
    setNnInteractiveLoadingCallback((loading) => setNnLoading(loading));
    setNnSessionActiveCallback((active) => setNnSessionActive(active));
    return () => {
      setNnInteractiveLoadingCallback(null);
      setNnSessionActiveCallback(null);
    };
  }, [setNnLoading, setNnSessionActive]);

  // Hanging protocol state
  const dispatchHangingProtocol = useSetAtom(hangingProtocolActionsAtom);
  const activeSlot = useAtomValue(activeSlotAtom);
  const activeSlotState = useAtomValue(slotStateFamily(activeSlot));
  const activeSlotSeriesId = activeSlotState.seriesId;
  const displayedSource = useAtomValue(displayedSourceFamily(activeSlot));
  const activeSourceUid = activeSlotState.reslice?.sourceSeriesId ?? activeSlotSeriesId;
  const activeObservationSource =
    displayedSource?.seriesInstanceUid === activeSourceUid ? displayedSource : null;

  // Get current study
  const currentStudy = worklist?.studies?.[currentIndex];
  const studyModelPrediction = useMemo(() => {
    const raw = currentStudy?.model_prediction as
      | {
          predictions?: NativePredictions;
          base_model_prediction?: NativePredictions;
          selected_series_id?: string;
          device_id?: string;
        }
      | undefined;
    const predictions =
      raw?.predictions ?? raw?.base_model_prediction ?? (raw as NativePredictions);
    if (!predictions || Object.keys(predictions).length === 0) return null;
    return {
      base_model_prediction: predictions,
      experimental_model_prediction: predictions,
      selectedSeriesId: raw?.selected_series_id ?? null,
      accessionNumber: null,
      metadata: {},
      device_id: raw?.device_id ?? "cohort-model",
    };
  }, [currentStudy?.model_prediction]);

  // Determine if this is a segmentation project (relies on worklist.project_type from backend).
  const isSegmentationProject =
    worklist?.project_type === "segmentation" ||
    worklist?.project_type === "both" ||
    worklist?.project_type === "segmentation_study";

  // Active viewport gating mode (the SSoT all layout components read). Seeded
  // per case from the case's assigned categories (FE schema mirror, B2), not
  // from a project-id prefix. The view-mode selector (B3) is the only other
  // writer; it never auto-changes. Because Jotai swallows same-value writes,
  // single-mode (or multi-same-mode) projects re-seed to the same value on
  // every case open → the layout never reloads. Hybrids re-seed to the
  // precedence default per case; the user overrides via the selector.
  const activeGatingMode = useAtomValue(projectGatingModeAtom);
  const setProjectGatingMode = useSetAtom(projectGatingModeAtom);
  const setAvailableGatingModes = useSetAtom(availableGatingModesAtom);
  const seededGatingUidRef = useRef<string | null>(null);
  const currentStudyUid = currentStudy?.study_instance_uid ?? null;
  useEffect(() => {
    if (!currentStudyUid || seededGatingUidRef.current === currentStudyUid) return;
    seededGatingUidRef.current = currentStudyUid;
    const modes = availableGatingModes(currentStudy?.labels_to_assign ?? []);
    setAvailableGatingModes(modes);
    setProjectGatingMode(defaultGatingMode(modes));
  }, [
    currentStudyUid,
    currentStudy?.labels_to_assign,
    setProjectGatingMode,
    setAvailableGatingModes,
  ]);

  // Fetch DICOM metadata for the current study.
  const {
    data: studyMetadata,
    isLoading: isLoadingMetadata,
    error: metadataError,
    progress: metadataProgress,
  } = useStudyMetadata(currentStudy?.study_instance_uid ?? "", projectId);

  // User-selected series from SeriesPicker (radiologist picks which series to load first).
  // Resets when study changes so the picker shows again for each new case.
  const [userSelectedSeriesId, setUserSelectedSeriesId] = useState<string | null>(null);
  useEffect(() => {
    setUserSelectedSeriesId(null);
  }, [currentStudy?.study_instance_uid]);

  // Auto-select the primary axial series for segmentation and reporting projects:
  // the axial series with >15 slices that has accompanying coronal/sagittal views.
  // Classification projects skip this and show the SeriesPicker for annotation.
  const isReportingProject = worklist?.project_type === "reporting";
  const autoSelectedSeriesId = useMemo(() => {
    if (!isSegmentationProject && !isReportingProject) return null;
    if (!studyMetadata?.series_metadata) return null;
    const seriesMeta = Object.values(studyMetadata.series_metadata);
    // Backend may omit `plane`; `derivePlane` falls back to ImageOrientationPatient.
    const withPlane = seriesMeta.map((s) => ({ s, plane: derivePlane(s) }));
    const hasCompanionViews = withPlane.some(
      ({ plane }) => plane === "Coronal" || plane === "Sagittal",
    );

    // Find axial series with >15 slices, prefer the one with companion views
    const axialCandidates = withPlane
      .filter(({ s, plane }) => plane === "Axial" && s.num_instances > 15)
      .sort((a, b) => b.s.num_instances - a.s.num_instances);

    if (axialCandidates.length > 0 && hasCompanionViews) {
      return axialCandidates[0].s.series_uid;
    }
    if (axialCandidates.length > 0) {
      return axialCandidates[0].s.series_uid;
    }
    return null;
  }, [studyMetadata, isSegmentationProject, isReportingProject]);

  const prioritySeriesId = userSelectedSeriesId ?? autoSelectedSeriesId;

  // Window preset handler - syncs across all hanging protocol slots
  const handleWindowPreset = useCallback(
    (_preset: WindowPreset, center: number, width: number) => {
      dispatchHangingProtocol({ type: "SET_ALL_WINDOWING", center, width });
    },
    [dispatchHangingProtocol],
  );

  // Series click handler - assigns clicked series to active slot
  const handleSeriesSelect = useCallback(
    (seriesId: string) => {
      if (!studyMetadata?.series_metadata) return;

      const seriesMeta = studyMetadata.series_metadata[seriesId];
      const windowing = seriesMeta ? getSeriesWindowing(seriesMeta) : undefined;

      const seriesInstances = studyMetadata.series?.[seriesId];
      warnOnMixedSignedTransferSyntax(seriesId, seriesInstances);
      dispatchHangingProtocol({
        type: "SET_SERIES",
        slotId: activeSlot,
        seriesId,
        windowing,
        isInverted: shouldAutoInvert(seriesInstances),
      });
    },
    [activeSlot, studyMetadata?.series_metadata, studyMetadata?.series, dispatchHangingProtocol],
  );

  // Load worklist whenever query data changes (clears stale data from previous projects)
  useEffect(() => {
    if (worklistQuery.data) {
      setWorklist(worklistQuery.data);
    }
  }, [worklistQuery.data, setWorklist]);

  // Initialize case from URL parameters — re-runs when worklist or URL params change
  useEffect(() => {
    if (!worklist) return;

    // Priority 1: study parameter (study_instance_uid for bookmarking)
    const studyParam = searchParams.get("study");
    if (studyParam) {
      const studyIndex = worklist.studies.findIndex((s) => s.study_instance_uid === studyParam);
      if (studyIndex >= 0) {
        setCurrentIndex(studyIndex);
        return;
      }
    }

    // Priority 2: case parameter (index)
    const caseParam = searchParams.get("case");
    if (caseParam) {
      const caseIndex = parseInt(caseParam, 10);
      if (!isNaN(caseIndex) && caseIndex >= 0 && caseIndex < worklist.studies.length) {
        setCurrentIndex(caseIndex);
      }
    }
  }, [worklist, searchParams, setCurrentIndex]);

  // Auto-jump to first unannotated case on entry when no URL case/study is
  // specified — so opening a project drops you at the next case to do, not
  // case 1. Fires once per project entry and stays at 0 if all are complete.
  const autoJumpedForProjectRef = useRef<string | null>(null);

  // Update URL helper - includes project, case index and study UID for bookmarking
  const updateUrl = useCallback(
    (index: number, studyUid: string) => {
      const url = `/labeling?project=${encodeURIComponent(projectId)}&case=${index}&study=${encodeURIComponent(studyUid)}`;
      window.history.pushState({}, "", url);
    },
    [projectId],
  );

  // Navigation
  const hasNext = currentIndex < (worklist?.studies.length || 0) - 1;
  const hasPrevious = currentIndex > 0;

  const handleNext = useCallback(() => {
    if (hasNext && worklist) {
      const newIndex = currentIndex + 1;
      setCurrentIndex(newIndex);
      updateUrl(newIndex, worklist.studies[newIndex].study_instance_uid);
    }
  }, [hasNext, worklist, currentIndex, setCurrentIndex, updateUrl]);

  const handlePrevious = useCallback(() => {
    if (hasPrevious && worklist) {
      const newIndex = currentIndex - 1;
      setCurrentIndex(newIndex);
      updateUrl(newIndex, worklist.studies[newIndex].study_instance_uid);
    }
  }, [hasPrevious, worklist, currentIndex, setCurrentIndex, updateUrl]);

  // Fetch case completion statuses for "next unlabeled" navigation
  const { data: casesData } = useCasesQuery(projectId, "all");

  // Auto-jump to first unannotated case: fires once per project when worklist
  // + cases data are loaded and there's no URL case/study pin.
  useEffect(() => {
    if (!worklist || !casesData) return;
    if (autoJumpedForProjectRef.current === projectId) return;
    if (searchParams.get("case") || searchParams.get("study")) {
      // URL pinned a case — treat as user intent, skip the jump.
      autoJumpedForProjectRef.current = projectId;
      return;
    }
    autoJumpedForProjectRef.current = projectId;

    const caseMap = new Map(casesData.cases.map((c) => [c.case_id, c]));
    const firstIncompleteIdx = worklist.studies.findIndex((study) => {
      const status = caseMap.get(study.case_id);
      return !status || !isSkippedByNavigation(status.completion_status);
    });
    if (firstIncompleteIdx > 0) {
      setCurrentIndex(firstIncompleteIdx);
      updateUrl(firstIncompleteIdx, worklist.studies[firstIncompleteIdx].study_instance_uid);
    }
  }, [worklist, casesData, projectId, searchParams, setCurrentIndex, updateUrl]);

  // Track time spent on each case (flushes on case change / unload)
  const isCaseComplete = useMemo(() => {
    if (!casesData || !currentStudy) return false;
    const caseStatus = casesData.cases.find((c) => c.case_id === currentStudy.case_id);
    return isFinishedCaseStatus(caseStatus?.completion_status);
  }, [casesData, currentStudy]);
  // Segmentation study: lock the cards while complete or flagged unassessable by this reader.
  const callerEmail = useCallerEmail();
  const lockReason = useMemo(() => {
    if (!casesData || !currentStudy) return null;
    const caseStatus = casesData.cases.find((c) => c.case_id === currentStudy.case_id);
    return caseLockReason(caseStatus, callerEmail);
  }, [casesData, currentStudy, callerEmail]);
  // Which viewer renders (study / factory / standalone …). Computed here so the
  // case-time hooks below know whether this is the segmentation study route.
  const viewerRoute = resolveLabelingViewerRoute({
    loading: worklistQuery.isLoading,
    error: worklistQuery.error,
    worklist,
    hasCurrentStudy: !!currentStudy,
    readOnly: annotationReadOnly,
  });
  // Declare the active viewer surface(s) so the toolbar, context menu, help
  // dialog and the segmentation camera-snap gate match this project type.
  // Defaults to condition labeling until the worklist loads. The factory and
  // study routes render CaseViewer, which mounts later and writes the atom
  // itself, so it gets these surfaces as `baseSurfaces` and is their only
  // writer there; a second writer here would be overwritten (ENG-250: the
  // camera snapped on the segmentation routes).
  const viewerSurfaces = useMemo(
    () => labelingViewerSurfaces(worklist?.project_type, annotationReadOnly),
    [worklist?.project_type, annotationReadOnly],
  );
  useLabelingViewerSurfaces(viewerSurfaces, viewerRoute === "factory" || viewerRoute === "study");
  // ENG-202: segmentation study case time is an event log (the pill and
  // analytics derive seconds from it); other routes keep the legacy timer.
  const caseEventLog = useCaseEventLog({
    enabled: viewerRoute === "study",
    projectId,
    studyUid: currentStudy?.study_instance_uid ?? "",
    userId: userId ?? null,
    // Unknown until the case statuses load: no events before that.
    caseClosed: casesData ? isCaseComplete : null,
  });
  // Above the event log's provider, so the log is passed explicitly.
  const markCompleteMutation = useMarkCompleteMutation(
    projectId,
    currentStudy?.study_instance_uid ?? "",
    caseEventLog,
  );
  useCaseSessionTimer(
    projectId,
    currentStudy?.study_instance_uid ?? "",
    isCaseComplete,
    // The study route records events instead (no dual write).
    !annotationReadOnly && !!worklistQuery.data && viewerRoute !== "study",
  );

  // ============================================================================
  // Reader-study wiring (Cortex v147 pilot). Activated when the case-viewer
  // URL carries ?schedule=<schedule_id> AND the rad has an assignment row for
  // the currently-open study. Outside study mode every effect below short-
  // circuits; the hook calls stay mounted (React rules of hooks) but never
  // fire a POST.
  // ============================================================================
  // Fall back to projectId when the URL doesn't carry an explicit ?schedule=
  // param. The pilot's schedule_id equals its project_id; clicking through
  // ReportingStudyHome doesn't carry an extra query param, so the implicit
  // mapping lets study-mode activate without further URL plumbing. Non-
  // reader-study reporting projects have no assignment rows in DDB, so
  // useStudyAssignment returns empty and studyMode stays false.
  const scheduleId = searchParams.get("schedule") ?? projectId;
  const { data: assignments } = useStudyAssignment(scheduleId);
  const currentAssignment: StudyAssignmentRow | null = useMemo(
    () =>
      currentStudy ? findAssignmentForStudy(assignments, currentStudy.study_instance_uid) : null,
    [assignments, currentStudy],
  );
  const studyMode = currentAssignment !== null;

  // washout=0 schedules emit both sessions with the same `earliest_open` —
  // the Session 1/2 badge is then a label without consequence (no real
  // washout between halves). Hide it when there's a single open timestamp
  // across the rad's rows, keep it when sessions are gated.
  //
  // Edge case: a real-washout schedule with exactly 1 assignment row here
  // is undecidable from earliest_open alone — current pilots are ≥10
  // studies, not hit in practice.
  const hasWashout = useMemo(() => {
    if (!assignments || assignments.length === 0) return false;
    const opens = new Set(assignments.map((a) => a.earliestOpen));
    return opens.size >= 2;
  }, [assignments]);

  // useStudyEventLog is always mounted (hook rules) but every method no-ops
  // when ctx is null. Gating at the hook itself means we don't have to
  // guard every callsite — a future caller that forgets `if (!studyMode)`
  // silently drops the event instead of POSTing garbage with a placeholder
  // cohort.
  const studyLog = useStudyEventLog(currentAssignment);
  const readerReport = useReportState(
    authToken,
    userId ?? null,
    currentStudy ? studyRowId(currentStudy) : null,
    {
      enabled: !!currentAssignment && !currentAssignment.isPractice,
      readerScheduleId: currentAssignment?.scheduleId,
    },
  );
  const readerIsSigned =
    !!currentAssignment && !currentAssignment.isPractice && !!readerReport.data?.is_report_signed;
  const readerScope = JSON.stringify([
    userId,
    currentAssignment?.scheduleId,
    currentAssignment?.studyUid,
  ]);
  const readerScopeRef = useRef(readerScope);
  readerScopeRef.current = readerScope;

  const [studyHasStarted, setStudyHasStarted] = useState(false);
  const [studyTimerStartedAt, setStudyTimerStartedAt] = useState<number | null>(null);
  const [studyTimerPaused, setStudyTimerPaused] = useState(false);
  const [studySigned, setStudySigned] = useState(false);
  const [studySignConfirmed, setStudySignConfirmed] = useState(false);
  const [surveyOpen, setSurveyOpen] = useState(false);
  const [practiceGateOpen, setPracticeGateOpen] = useState(false);
  const firstInteractionFiredRef = useRef(false);

  // Authoritative idle-subtracted active reading time. Always mounted (hook
  // rules) but start/pause/resume/reset are only called in study mode.
  const activeReadingTimer = useActiveReadingTimer();

  // Case-open mark for SILENT practice-case timing (case open → sign wall
  // clock). Stamped on every case open below; read only for practice cases at
  // sign. performance.now() is monotonic and continuous across SPA navigation,
  // so the delta stays valid even when the reader routes worklist → case.
  const practiceOpenRef = useRef<{ mark: number; iso: string } | null>(null);

  // case_opened — fires once per study_uid transition in study mode. Also
  // re-locks the case (Start gate appears again) for the next case. Keyed
  // on the study_uid *string* (not the currentStudy object) so the effect
  // doesn't re-fire when the worklist row re-allocates on background
  // refetches. (`currentStudyUid` is declared once above, near the gating seed.)
  useEffect(() => {
    if (!studyMode || !currentStudyUid) return;
    setStudyHasStarted(false);
    setStudyTimerStartedAt(null);
    setStudyTimerPaused(false);
    setStudySigned(false);
    setStudySignConfirmed(false);
    setSurveyOpen(false);
    setPracticeGateOpen(false);
    firstInteractionFiredRef.current = false;
    activeReadingTimer.reset();
    // Anchor for silent practice timing. Set for every case (used only for
    // practice at sign); a re-open re-stamps it so each attempt is measured
    // from its own open.
    practiceOpenRef.current = {
      mark: performance.now(),
      iso: new Date().toISOString(),
    };
    studyLog
      .logEvent("case_opened", { studyUid: currentStudyUid })
      .catch((err) => console.warn("[study] case_opened POST failed", err));
  }, [studyMode, currentStudyUid, readerScope, studyLog, activeReadingTimer]);

  useEffect(() => {
    if (!readerIsSigned) return;
    setStudySigned(true);
    setStudyHasStarted(true);
    setStudyTimerPaused(true);
    activeReadingTimer.pause();
  }, [readerIsSigned, readerScope, activeReadingTimer]);

  // first_interaction — one-shot document pointerdown after Start. Captures
  // the first deliberate input even if it lands on the viewer (not the editor).
  useEffect(() => {
    if (!studyMode || !studyHasStarted) return;
    const onPointer = () => {
      if (firstInteractionFiredRef.current) return;
      firstInteractionFiredRef.current = true;
      studyLog
        .logEvent("first_interaction")
        .catch((err) => console.warn("[study] first_interaction POST failed", err));
    };
    document.addEventListener("pointerdown", onPointer, {
      once: true,
      capture: true,
    });
    return () => document.removeEventListener("pointerdown", onPointer, { capture: true });
  }, [studyMode, studyHasStarted, studyLog]);

  // Idle / active edge events. Disabled until Start so the lock-screen
  // doesn't generate spurious idle events. Also disabled after sign so
  // post-sign mouse movement doesn't unfreeze the locked-in elapsed time.
  useIdleTimer({
    thresholdMs: 30_000,
    disabled: !studyMode || !studyHasStarted || studySigned || studySignConfirmed,
    onIdle: () => {
      setStudyTimerPaused(true);
      activeReadingTimer.pause();
      studyLog
        .logEvent("timer_paused")
        .catch((err) => console.warn("[study] timer_paused POST failed", err));
    },
    onActive: () => {
      setStudyTimerPaused(false);
      activeReadingTimer.resume();
      studyLog
        .logEvent("timer_resumed")
        .catch((err) => console.warn("[study] timer_resumed POST failed", err));
    },
  });

  const handleStudyStart = useCallback(() => {
    setStudyHasStarted(true);
    // Monotonic clock — performance.now() doesn't jump on system clock
    // adjustment. The pill consumes the same scale (no Date.now mixing).
    setStudyTimerStartedAt(performance.now());
    activeReadingTimer.start();
    studyLog
      .logEvent("timer_started")
      .catch((err) => console.warn("[study] timer_started POST failed", err));
  }, [studyLog, activeReadingTimer]);

  // Deferred-promise gate for the difficulty modal. handleConfirmSign opens
  // the modal and returns a promise; modal submit / cancel calls resolve.
  // Keyed on a ref so the modal handlers stay stable across renders.
  const [difficultyModalOpen, setDifficultyModalOpen] = useState(false);
  const difficultyResolverRef = useRef<((result: StudySignConfirmation) => void) | null>(null);

  useEffect(() => {
    setDifficultyModalOpen(false);
    return () => {
      difficultyResolverRef.current?.(null);
      difficultyResolverRef.current = null;
    };
  }, [readerScope]);

  const handleConfirmSign = useCallback<() => Promise<StudySignConfirmation>>(() => {
    return new Promise<StudySignConfirmation>((resolve) => {
      difficultyResolverRef.current = resolve;
      setDifficultyModalOpen(true);
    });
  }, []);

  const handleDifficultySubmit = useCallback(
    (difficulty: number) => {
      // Freeze measured reading time before the final autosave round trip.
      activeReadingTimer.pause();
      setStudyTimerPaused(true);
      setStudySignConfirmed(true);
      const activeReadingMs = activeReadingTimer.readActiveMs();
      void studyLog
        .logEvent("timer_paused")
        .catch((err) => console.warn("[study] sign pause failed", err));
      setDifficultyModalOpen(false);
      difficultyResolverRef.current?.({ difficulty, activeReadingMs });
      difficultyResolverRef.current = null;
    },
    [activeReadingTimer, studyLog],
  );

  const handleDifficultyCancel = useCallback(() => {
    setDifficultyModalOpen(false);
    difficultyResolverRef.current?.(null);
    difficultyResolverRef.current = null;
  }, []);

  const handleStudyInitialDoc = useCallback(
    (ctx: StudyInitialDocContext) => {
      // Hook is the gate — no-ops when ctx is null. Wired unconditionally
      // through PrototypeReport's onInitialDoc so the snapshot still lands
      // for a non-study case ReportingRightPanel rebuilds against; the call
      // is a cheap no-op for non-study mode.
      studyLog
        .logSnapshot("initial", ctx.reportHtml, ctx.templateId, ctx.templateCharCount)
        .catch((err) => console.warn("[study] initial snapshot POST failed", err));
    },
    [studyLog],
  );

  // The reader-session snapshot is the signing authority. Late responses
  // must not change the timer or survey of a subsequently opened case.
  const handleStudySigned = useCallback(
    async (ctx: StudySignContext) => {
      if (!studyMode) return;
      const signingScope = readerScope;
      if (ctx.difficulty === undefined) {
        // Reachable only if `confirmSign` somehow got bypassed; the BE sign
        // schema requires a difficulty so stop before sending.
        throw new Error("[study] sign ctx missing difficulty — wiring bug");
      }
      // Pause the active timer at the instant of sign to capture the final
      // idle-subtracted elapsed before the audit POST flies.
      activeReadingTimer.pause();
      const activeMs = ctx.activeReadingMs ?? activeReadingTimer.readActiveMs();
      await studyLog.signStudy(
        ctx.expectedRevision ?? "",
        ctx.templateId,
        ctx.templateCharCount,
        ctx.difficulty as Difficulty,
        activeMs,
      );
      if (readerScopeRef.current !== signingScope) return;
      // Freeze the timer at the signed instant ONLY after the audit row has
      // landed. `studySigned` also disables the idle-timer so post-sign mouse
      // movement can't clear the pause.
      setStudySigned(true);
      setStudyTimerPaused(true);

      // Silent wall-clock timing for practice/untimed cases (case open → sign).
      // Fire-and-forget: a failed beacon must never block or alter the sign the
      // reader sees, and the practice UI is unchanged. Skip (don't send a
      // fabricated 0) if the open mark is somehow missing.
      if (currentAssignment?.isPractice) {
        const open = practiceOpenRef.current;
        if (open) {
          studyLog
            .logPracticeTiming({
              wallClockMs: Math.round(performance.now() - open.mark),
              clientOpenTs: open.iso,
              clientSignTs: new Date().toISOString(),
            })
            .catch((err) => console.warn("[practice-timing] POST failed", err));
        } else {
          console.warn("[practice-timing] no open mark at sign — skipping beacon");
        }
      }

      // Practice accounting — increment counter so the gate knows how many
      // practice cases have been completed. The event log already no-ops for
      // practice cases (isPractice gate in useStudyEventLog) so this is the
      // only place we track practice completions.
      if (currentAssignment?.isPractice && onboardingPhase === "practice") {
        setPracticeSignedCount((n) => n + 1);
      }

      // Open the post-case survey immediately after sign — the two Likert
      // ratings are protocol-required before the rad can proceed.
      setSurveyOpen(true);
    },
    [
      studyMode,
      readerScope,
      studyLog,
      activeReadingTimer,
      currentAssignment,
      onboardingPhase,
      setPracticeSignedCount,
    ],
  );

  // Practice / study phase handlers
  const handleBeginStudy = useCallback(() => {
    setOnboardingPhase("study" as OnboardingPhase);
  }, [setOnboardingPhase]);

  const handleMorePractice = useCallback(() => {
    // Close the gate — the rad can read another practice case before starting.
    setPracticeGateOpen(false);
  }, []);

  // Post-case survey handlers — submit the two Likert ratings via the existing
  // `useSubmitReaderFeedback` mutation, then close the modal. Skip must emit a
  // logged signal (survey_skipped) so biostats can see the omission rate.
  const handleSurveySubmit = useCallback(
    async (confidence: number, mentalDemand: number) => {
      if (!userId || !currentStudy) {
        // Don't leave the survey hung open silently — log the bail and close.
        console.warn("[study] survey submit skipped — missing userId/currentStudy; closing survey");
        setSurveyOpen(false);
        return;
      }
      // Feedback writes the SAME DDB study row the reporter signs into (keyed
      // by the compound id when the case has a job_id), so it must derive the
      // id the same way — see studyRowId. The bare UID targets a non-existent
      // row and the backend 404s silently.
      // Practice cases: still collect ratings (the rad needs the experience)
      // but only submit to the backend for real study cases.
      if (!currentAssignment?.isPractice) {
        await new Promise<void>((resolve, reject) => {
          submitFeedbackMutation.mutate(
            {
              userId,
              studyId: studyRowId(currentStudy),
              readerConfidence: confidence,
              mentalDemand,
            },
            { onSuccess: () => resolve(), onError: (err) => reject(err) },
          );
        });
      }
      setSurveyOpen(false);
      // After a practice case's survey, open the gate if the minimum is met.
      if (currentAssignment?.isPractice && onboardingPhase === "practice" && canBeginStudy) {
        setPracticeGateOpen(true);
      }
    },
    [
      userId,
      currentStudy,
      currentAssignment,
      submitFeedbackMutation,
      onboardingPhase,
      canBeginStudy,
    ],
  );

  const handleSurveySkip = useCallback(() => {
    setSurveyOpen(false);
    // Emit a logged skip signal so biostats can see the omission rate.
    // Callers depend on this NOT being a silent discard.
    studyLog
      .logEvent("survey_skipped")
      .catch((err) => console.warn("[study] survey_skipped POST failed", err));
  }, [studyLog]);

  // No-AI arm forces the existing blank-template path so SuggestionStrip
  // and Inline AI chips never mount. AI suppression by arm is the only
  // arm-driven UI change in this phase.
  const studyTemplateModeOverride = currentAssignment?.arm === "without_ai" ? "blank" : undefined;

  const navigateTo = useCallback(
    (index: number) => {
      if (!worklist) return;
      setCurrentIndex(index);
      updateUrl(index, worklist.studies[index].study_instance_uid);
    },
    [worklist, setCurrentIndex, updateUrl],
  );
  const { hasNextUnlabeled, goToNextUnlabeled: handleNextUnlabeled } = useNextUnlabeledCase(
    projectId,
    worklist?.studies,
    currentIndex,
    navigateTo,
  );

  // Register keyboard shortcuts (all navigation + viewer hotkeys)
  useViewerHotkeys({
    onPresetSelect: handleWindowPreset,
    // The study form is saved as one record; do not skip its save boundary.
    onPrevious: worklist?.study_observation_task ? undefined : handlePrevious,
    onNext: worklist?.study_observation_task ? undefined : handleNext,
    onNextUnlabeled: worklist?.study_observation_task ? undefined : handleNextUnlabeled,
    enabled: !annotationReadOnly && viewerRoute === "standalone",
  });

  // Loading/error/empty states
  if (viewerRoute === "loading") {
    return <FullScreenLoading message="Loading worklist..." />;
  }
  if (viewerRoute === "joint-review") {
    return <Navigate to={listUrl(projectId)} replace />;
  }
  if (viewerRoute === "error") {
    return (
      <FullScreenError
        title="Failed to load worklist"
        message={worklistQuery.error instanceof Error ? worklistQuery.error.message : undefined}
      />
    );
  }
  if (!worklist || worklist.studies.length === 0) {
    return <FullScreenEmpty message="No studies found in worklist" />;
  }
  if (!currentStudy) {
    return <FullScreenError title="Invalid study index" />;
  }
  if (viewerRoute === "invalid-anatomy") {
    return (
      <FullScreenError
        title="Invalid anatomy worklist configuration"
        message="The anatomy task requires a classification worklist."
      />
    );
  }

  if (viewerRoute === "factory") {
    const studyId = studyRowId(currentStudy);
    return (
      <CaseViewer
        key={studyId}
        studyData={{ studyId }}
        enableSegmentation
        projectId={projectId}
        modelPredictionOverride={studyModelPrediction}
        disableReportQueries
        baseSurfaces={viewerSurfaces}
        factorySegmentationContext={{
          caseId: currentStudy.case_id,
          annotationConditions: currentStudy.labels_to_assign,
          caseLabel:
            currentStudy.patient_id || currentStudy.accession
              ? `${currentStudy.patient_id} · ${currentStudy.accession}`
              : (currentStudy.study_description ?? currentStudy.study_instance_uid),
          currentIndex,
          totalCases: worklist.studies.length,
          hasPrevious,
          hasNext,
          isComplete: isCaseComplete,
          isMarkingComplete: markCompleteMutation.isPending,
          onPrevious: handlePrevious,
          onNext: handleNext,
          onNextUnlabeled: handleNextUnlabeled,
          hasNextUnlabeled,
          onWorklist: handleWorkList,
          onMarkComplete: () => markCompleteMutation.mutate(undefined),
        }}
      />
    );
  }

  if (viewerRoute === "study") {
    const studyId = studyRowId(currentStudy);
    const studyViewer = (
      <CaseViewer
        key={studyId}
        studyData={{ studyId }}
        enableSegmentation
        projectId={projectId}
        modelPredictionOverride={studyModelPrediction}
        disableReportQueries
        baseSurfaces={viewerSurfaces}
        segmentationStudyContext={{
          templateMode: currentStudy.template_mode ?? "blank",
          annotationConditions: currentStudy.labels_to_assign,
          caseLabel:
            currentStudy.patient_id || currentStudy.accession
              ? `${currentStudy.patient_id} · ${currentStudy.accession}`
              : (currentStudy.study_description ?? currentStudy.study_instance_uid),
          currentIndex,
          totalCases: worklist.studies.length,
          hasPrevious,
          hasNext,
          isComplete: isCaseComplete,
          isMarkingComplete: markCompleteMutation.isPending,
          onPrevious: handlePrevious,
          onNext: handleNext,
          onNextUnlabeled: handleNextUnlabeled,
          hasNextUnlabeled,
          onWorklist: () =>
            navigate(`/labeling/segmentation-study/${encodeURIComponent(projectId)}`),
          onMarkComplete: () => markCompleteMutation.mutate(undefined),
          lockReason,
        }}
      />
    );
    // Above the per-study CaseViewer (key={studyId}) so a case change keeps the queue.
    return (
      <CaseEventLogContext.Provider value={caseEventLog}>
        {studyViewer}
      </CaseEventLogContext.Provider>
    );
  }

  return (
    <div className="relative flex h-full bg-black" data-testid="viewer-container">
      {/* Left Sidebar */}
      {leftSidebarCollapsed ? (
        <CollapsedSidebarStrip
          side="left"
          onExpand={() => setLeftSidebarCollapsed(false)}
          shortcutHint=","
        />
      ) : (
        <LeftSidebar
          studyMetadata={studyMetadata}
          studyId={currentStudy.study_instance_uid}
          activeWindowPreset={null}
          onSeriesSelect={handleSeriesSelect}
          onWindowPresetSelect={handleWindowPreset}
          isCollapsed={leftSidebarCollapsed}
          onToggleCollapse={() => setLeftSidebarCollapsed((prev) => !prev)}
          footerExtra={
            canViewDashboard ? (
              <button
                onClick={() => setShowDashboard(!showDashboard)}
                className={`sidebar-btn w-full px-2 py-2 text-[10px] font-medium gap-1.5 ${
                  showDashboard
                    ? "text-signal-text-tint bg-signal-tint"
                    : "text-fg-muted hover:text-fg"
                }`}
              >
                <span>{showDashboard ? "Back to Viewer" : "Stats Dashboard"}</span>
              </button>
            ) : undefined
          }
        />
      )}

      {/* Center: Dashboard or Multi-viewport grid */}
      <div className="flex-1 bg-black">
        {showDashboard ? (
          <AnnotationSpeedDashboard projectId={projectId} />
        ) : isLoadingMetadata ? (
          <ViewerLoading
            title={`Loading Study ${currentIndex + 1}/${worklist.studies.length}`}
            subtitle={formatMetadataProgress(metadataProgress)}
            progress={
              metadataProgress.instancesRead !== undefined &&
              metadataProgress.totalSeries !== undefined
                ? {
                    current: metadataProgress.seriesIndex || 0,
                    total: metadataProgress.totalSeries,
                    stage: formatMetadataProgress(metadataProgress),
                  }
                : undefined
            }
          />
        ) : metadataError ? (
          <ViewerError
            title="Failed to load DICOM metadata"
            message={metadataError instanceof Error ? metadataError.message : undefined}
            actions={
              worklist.study_observation_task ? undefined : (
                <NavigationButtons
                  onPrevious={handlePrevious}
                  onNext={handleNext}
                  onNextUnlabeled={handleNextUnlabeled}
                  hasPrevious={hasPrevious}
                  hasNext={hasNext}
                  hasNextUnlabeled={hasNextUnlabeled}
                />
              )
            }
          />
        ) : studyMetadata && !prioritySeriesId ? (
          <SeriesPicker
            studyMetadata={studyMetadata}
            onSeriesSelect={setUserSelectedSeriesId}
            title="Select primary series for annotation"
          />
        ) : studyMetadata &&
          !worklist.study_observation_task &&
          activeGatingMode === "slice_dependent" &&
          prioritySeriesId ? (
          <SliceDependentGrid
            studyId={currentStudy.study_instance_uid}
            studyMetadata={studyMetadata}
            axialSeriesId={prioritySeriesId}
          />
        ) : studyMetadata ? (
          activeGatingMode === GATING_MODE.PHASE_DEPENDENT ? (
            <PhaseDependentLayout
              blindAnatomyTask={worklist.study_observation_task !== undefined}
              studyId={currentStudy.study_instance_uid}
              studyMetadata={studyMetadata}
              prioritySeriesId={prioritySeriesId}
              isSegmentationProject={isSegmentationProject && !annotationReadOnly}
              filterNonKidneyAnatomy={US3D_REGEX.test(projectId)}
            />
          ) : (
            <HangingProtocolGrid
              blindAnatomyTask={worklist.study_observation_task !== undefined}
              studyId={currentStudy.study_instance_uid}
              studyMetadata={studyMetadata}
              prioritySeriesId={prioritySeriesId}
              isSegmentationProject={isSegmentationProject && !annotationReadOnly}
              filterNonKidneyAnatomy={US3D_REGEX.test(projectId)}
            />
          )
        ) : (
          <ViewerError title="No DICOM data available" />
        )}
      </div>

      {/* Right: Labeling or Adjudication Panel */}
      {rightSidebarCollapsed ? (
        <CollapsedSidebarStrip
          side="right"
          onExpand={() => setRightSidebarCollapsed(false)}
          shortcutHint="."
        />
      ) : (
        <div
          className={`shrink-0 h-full overflow-hidden ${
            worklist.project_type === "reporting" ? "relative" : "w-80"
          }`}
          style={worklist.project_type === "reporting" ? { width: reportPaneWidth } : undefined}
        >
          {/* Drag handle to resize the reporting drafter pane (left edge). */}
          {worklist.project_type === "reporting" && (
            <div
              onPointerDown={handleReportPaneResize}
              className="absolute left-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-primary/40 active:bg-primary/60 z-40"
              title="Drag to resize"
              role="separator"
              aria-orientation="vertical"
            />
          )}
          {annotationReadOnly && (
            <div role="status" className="border-b border-border p-4 text-sm text-fg-muted">
              <p>Annotation paused. Existing work is available to view.</p>
              <div className="mt-3 flex gap-3">
                <button type="button" onClick={handlePrevious} disabled={!hasPrevious}>
                  Previous case
                </button>
                <button type="button" onClick={handleNext} disabled={!hasNext}>
                  Next case
                </button>
                <button type="button" onClick={handleWorkList}>
                  Worklist
                </button>
              </div>
            </div>
          )}
          <fieldset
            disabled={annotationReadOnly}
            className="contents"
            aria-label="Annotation controls"
          >
            {worklist.study_observation_task !== undefined ? (
              <StudyObservationPanel
                projectId={projectId}
                caseId={currentStudy.case_id}
                studyInstanceUid={currentStudy.study_instance_uid}
                activeSource={activeObservationSource}
                seriesLabels={Object.fromEntries(
                  Object.entries(studyMetadata?.series_metadata ?? {}).map(([uid, s]) => [
                    uid,
                    `Series ${s.series_number}${s.series_description ? ` · ${s.series_description}` : ""}`,
                  ]),
                )}
                readOnly={annotationReadOnly}
                currentIndex={currentIndex}
                totalCases={worklist.studies.length}
                hasNext={hasNext}
                hasPrevious={hasPrevious}
                onNext={handleNext}
                onPrevious={handlePrevious}
                onWorkList={handleWorkList}
              />
            ) : worklist.project_type === "reporting" ? (
              <ReportingRightPanel
                worklist={worklist}
                studyIndex={currentIndex}
                studyMetadata={studyMetadata}
                onPrevious={handlePrevious}
                onNext={handleNext}
                hasPrevious={hasPrevious}
                hasNext={hasNext}
                templateModeOverride={studyTemplateModeOverride}
                readerContext={currentAssignment ?? undefined}
                onSigned={studyMode ? handleStudySigned : undefined}
                onInitialDoc={handleStudyInitialDoc}
                confirmSign={studyMode ? handleConfirmSign : undefined}
                studyTimerStartedAt={studyMode ? studyTimerStartedAt : undefined}
                studyTimerPaused={studyTimerPaused}
                studyIsPractice={!!currentAssignment?.isPractice}
              />
            ) : worklist.label_schema?.type === "attribute_labeling" ? (
              <AttributeLabelingPanel
                projectId={projectId}
                caseId={currentStudy.case_id}
                studyInstanceUid={currentStudy.study_instance_uid}
                labelsToAssign={currentStudy.labels_to_assign}
                attributeDefinitions={worklist.label_schema.attribute_definitions ?? {}}
                onNext={handleNext}
                onPrevious={handlePrevious}
                onNextUnlabeled={handleNextUnlabeled}
                onWorkList={handleWorkList}
                hasNext={hasNext}
                hasPrevious={hasPrevious}
                hasNextUnlabeled={hasNextUnlabeled}
                currentIndex={currentIndex}
                totalCases={worklist.studies.length}
              />
            ) : currentStudy.reader_labels ? (
              <AdjudicationPanel
                readOnly={annotationReadOnly}
                projectId={projectId}
                caseId={currentStudy.case_id}
                studyInstanceUid={currentStudy.study_instance_uid}
                labelsToAssign={currentStudy.labels_to_assign}
                readerLabels={currentStudy.reader_labels}
                disagreements={currentStudy.disagreements ?? []}
                onNext={handleNext}
                onPrevious={handlePrevious}
                onNextUnlabeled={handleNextUnlabeled}
                onWorkList={handleWorkList}
                hasNext={hasNext}
                hasPrevious={hasPrevious}
                hasNextUnlabeled={hasNextUnlabeled}
                currentIndex={currentIndex}
                totalCases={worklist.studies.length}
                instructionsKey={worklist.instructions_key ?? null}
              />
            ) : isSegmentationProject ? (
              <SegmentationAnnotationPanel
                projectId={projectId}
                caseId={currentStudy.case_id}
                studyInstanceUid={currentStudy.study_instance_uid}
                seriesInstanceUid={activeSlotSeriesId ?? undefined}
                axialSeriesUid={prioritySeriesId ?? undefined}
                onNext={handleNext}
                onPrevious={handlePrevious}
                onNextUnlabeled={handleNextUnlabeled}
                onWorkList={handleWorkList}
                hasNext={hasNext}
                hasPrevious={hasPrevious}
                hasNextUnlabeled={hasNextUnlabeled}
                currentIndex={currentIndex}
                totalCases={worklist.studies.length}
                gtHints={currentStudy.gt_hints}
                labelsToAssign={currentStudy.labels_to_assign}
                instructionsKey={worklist.instructions_key ?? null}
              />
            ) : (
              <LabelingPanel
                readOnly={annotationReadOnly}
                projectId={projectId}
                caseId={currentStudy.case_id}
                studyInstanceUid={currentStudy.study_instance_uid}
                labelsToAssign={currentStudy.labels_to_assign}
                onNext={handleNext}
                onPrevious={handlePrevious}
                onNextUnlabeled={handleNextUnlabeled}
                onWorkList={handleWorkList}
                hasNext={hasNext}
                hasPrevious={hasPrevious}
                hasNextUnlabeled={hasNextUnlabeled}
                currentIndex={currentIndex}
                totalCases={worklist.studies.length}
                instructionsKey={worklist.instructions_key ?? null}
                labelOptions={worklist.label_schema?.options ?? null}
                optionsByLabel={worklist.label_schema?.options_by_label ?? null}
                optionAbbreviations={worklist.label_schema?.option_abbreviations ?? null}
                /* ONE-ROUND combined mode: when the worklist opts in via
                 * `single_select_with_attributes`, a Present call reveals +
                 * requires that finding's attributes inline. Undefined otherwise,
                 * so the plain single_select path is unchanged. */
                attributeDefinitions={
                  worklist.label_schema?.type === "single_select_with_attributes"
                    ? (worklist.label_schema.attribute_definitions ?? {})
                    : undefined
                }
                attributeCardinality={
                  worklist.label_schema?.type === "single_select_with_attributes"
                    ? worklist.label_schema.attribute_cardinality
                    : undefined
                }
                examDisposition={worklist.exam_disposition}
              />
            )}
          </fieldset>
        </div>
      )}

      {/* Reader-study lock-screen — overlays the whole viewer until the rad
       *  clicks Start. Above the navbar would be wrong; the navbar lives
       *  outside this component. */}
      {studyMode &&
        !studyHasStarted &&
        currentAssignment &&
        !readerIsSigned &&
        (readerReport.isSuccess || currentAssignment.isPractice) && (
          <StudyLockScreen
            arm={currentAssignment.arm}
            sessionOrder={hasWashout ? currentAssignment.sessionOrder : undefined}
            isPractice={currentAssignment.isPractice}
            onStart={handleStudyStart}
            imagesLoading={isLoadingMetadata}
          />
        )}
      {difficultyModalOpen && (
        <StudyDifficultyModal onSubmit={handleDifficultySubmit} onCancel={handleDifficultyCancel} />
      )}
      {surveyOpen && studyMode && (
        <StudyPostCaseSurvey onSubmit={handleSurveySubmit} onSkip={handleSurveySkip} />
      )}
      {studyMode && onboardingPhase === "practice" && practiceGateOpen && (
        <StudyPracticeGate
          practiceSignedCount={practiceSignedCount}
          canBeginStudy={canBeginStudy}
          onBeginStudy={handleBeginStudy}
          onMorePractice={handleMorePractice}
        />
      )}
    </div>
  );
}
